import React, {
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";

import Box from "veneer/Box";
import BulkActionBar from "veneer/BulkActionBar";
import Button from "veneer/Button";
import { JollyRancherChip } from "veneer/Chip";
import DataTable, {
  AppliedFilter,
  AvailableFilterOption,
  BulkActionBarItem,
  ColumnDef,
  PageSize,
  createColumnHelper,
  sorting,
} from "veneer/DataTable";

import GroupByTable, {
  GroupedTableRow,
} from "cortex/ui/src/ui/table/component/GroupedTable";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import { User } from "cortex/ui/src/user/model";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

import IncidentPriority from "./IncidentPriority";
import IncidentStatus from "./IncidentStatus";
import {
  formatNotified,
  formatResolveDetails,
} from "./IncidentTableFormatters";

interface GroupedIncidentsTableProps {
  anchorRef: RefObject<HTMLDivElement>;
  appliedFilters?: {
    filters: ReadonlyArray<AppliedFilter>;
    onClearAll: () => void;
  };
  availableFilters?: {
    filters: ReadonlyArray<AvailableFilterOption>;
    onChange: (selectedFilterIds: ReadonlyArray<string>) => void;
    selectedFilterIds: ReadonlyArray<string>;
  };
  bulkActionBarItems: ReadonlyArray<BulkActionBarItem>;
  handleOpenResolveAll: (incidentsToResolve: ReadonlyArray<Incident>) => void;
  handleOpenSingle: (
    incident: Incident,
    showResolutionCommentByDefault?: boolean,
  ) => void;
  incidents: ReadonlyArray<Incident>;
  selectedItems: ReadonlyArray<Incident>;
  savedFilters?: {
    currentFilters: unknown;
    onApply: (filters: unknown) => void;
  };
  setSelectedItems: (items: ReadonlyArray<Incident>) => void;
  settingsKey?: string;
  sizePerPage: PageSize;
  type?: "resolved" | "unresolved";
  users: Record<string, User>;
  userIsReadonly: boolean;
  viewOptions?: ReactNode;
}

const column = createColumnHelper<Incident>();
const groupedColumn = createColumnHelper<GroupedTableRow<Incident>>();

const RESOLVED_PREFIX = "grouped-resolved-incidents";
const UNRESOLVED_PREFIX = "grouped-unresolved-incidents";

export default function GroupedIncidentsTable({
  anchorRef,
  appliedFilters,
  availableFilters,
  bulkActionBarItems,
  handleOpenResolveAll,
  handleOpenSingle,
  incidents,
  savedFilters,
  selectedItems,
  setSelectedItems,
  settingsKey,
  sizePerPage,
  type,
  users,
  userIsReadonly,
  viewOptions,
}: GroupedIncidentsTableProps) {
  const isResolvedTable = type === "resolved";
  const dataTestPrefix = isResolvedTable ? RESOLVED_PREFIX : UNRESOLVED_PREFIX;
  const showBulkActionBar = !isResolvedTable && !userIsReadonly;

  const handleNestedSelectionChange = useCallback(
    (
      groupItems: ReadonlyArray<Incident>,
      nextGroupSelectedItems: ReadonlyArray<Incident>,
    ) => {
      const groupIds = new Set(groupItems.map(({ id }) => String(id)));
      const selectedOutsideGroup = selectedItems.filter(
        ({ id }) => !groupIds.has(String(id)),
      );

      setSelectedItems([...selectedOutsideGroup, ...nextGroupSelectedItems]);
    },
    [selectedItems, setSelectedItems],
  );

  const nestedColumns = useMemo<ReadonlyArray<ColumnDef<Incident, any>>>(() => {
    const baseColumns = [
      column.accessor("device_name", {
        header: "Device",
        cell: (prop) => {
          const row = prop.row.original;
          return (
            <Link to={`/devices/${row.device_device_id}`}>
              {prop.getValue()}
            </Link>
          );
        },
        sortingFn: "alphanumeric",
        meta: {
          dataTestId: `${dataTestPrefix}-device`,
          width: 300,
        },
      }),
      column.accessor("status", {
        header: "Status",
        cell: (prop) => <IncidentStatus status={prop.getValue()} />,
        sortingFn: "alphanumeric",
      }),
      column.accessor("create_date", {
        header: "First Occurrence Date",
        cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
        sortingFn: sorting.datetime((row) => row.create_date),
        meta: {
          dataTestId: `${dataTestPrefix}-first-occurrence-date`,
          width: 200,
        },
      }),
      column.accessor("last_notified", {
        header: "Last Notified Date",
        cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
        sortingFn: sorting.datetime((row) => row.last_notified),
        meta: {
          dataTestId: `${dataTestPrefix}-last-notified-date`,
          width: 200,
        },
      }),
      column.accessor("notified", {
        header: "Notified",
        cell: (prop) => formatNotified(users, prop.row.original),
        sortingFn: "alphanumeric",
        meta: {
          dataTestId: `${dataTestPrefix}-notified`,
        },
      }),
      column.accessor("comment", {
        header: "Details",
        cell: (prop) => formatResolveDetails(users, prop.row.original),
        enableSorting: false,
      }),
    ];

    if (isResolvedTable) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        id: "resolve",
        header: "",
        cell: ({ row }) =>
          userIsReadonly ? null : (
            <Button
              dataTestId="resolve-button"
              icon="checkmark"
              onClick={() => handleOpenSingle(row.original, true)}
              variant="outlined"
            >
              Resolve
            </Button>
          ),
        meta: {
          dataTestId: `${dataTestPrefix}-resolve`,
          disableWrapper: true,
          width: 120,
        },
      },
    ];
  }, [
    dataTestPrefix,
    handleOpenSingle,
    isResolvedTable,
    userIsReadonly,
    users,
  ]);

  const groupedColumns = useMemo<
    ReadonlyArray<ColumnDef<GroupedTableRow<Incident>, any>>
  >(() => {
    const priorityColumn = groupedColumn.display({
      id: "priority",
      header: "Priority",
      cell: ({ row }) => (
        <IncidentPriority
          priority={row.original.representative.alert_priority_snapshot}
        />
      ),
      meta: {
        dataTestId: `${dataTestPrefix}-priority`,
        width: 150,
      },
    });

    if (isResolvedTable) {
      return [
        groupedColumn.accessor(
          (row) => row.representative.alert_name_snapshot ?? "",
          {
            id: "name",
            header: "Name",
            cell: (prop) => prop.getValue(),
            sortingFn: "alphanumeric",
            meta: {
              dataTestId: `${dataTestPrefix}-name`,
            },
          },
        ),
        priorityColumn,
        groupedColumn.accessor((row) => row.items.length, {
          id: "resolved",
          header: "Resolved",
          cell: (prop) => (
            <JollyRancherChip color="blue" label={prop.getValue()} />
          ),
          sortingFn: sorting.number((row) => row.items.length),
          meta: {
            align: "center",
            dataTestId: `${dataTestPrefix}-resolved`,
            width: 100,
          },
        }),
        groupedColumn.accessor((row) => getLastResolved(row.items), {
          id: "last_resolved",
          header: "Last Resolved",
          cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
          sortingFn: sorting.datetime((row) => getLastResolved(row.items)),
          meta: {
            dataTestId: `${dataTestPrefix}-last-resolved`,
            width: 200,
          },
        }),
      ];
    }

    return [
      groupedColumn.accessor(
        (row) => row.representative.alert_name_snapshot ?? "",
        {
          id: "name",
          header: "Name",
          cell: (prop) => prop.getValue(),
          sortingFn: "alphanumeric",
          meta: {
            dataTestId: `${dataTestPrefix}-name`,
          },
        },
      ),
      priorityColumn,
      groupedColumn.accessor((row) => getUniqueDeviceCount(row.items), {
        id: "devices",
        header: "Devices",
        cell: (prop) => (
          <JollyRancherChip color="blue" label={prop.getValue()} />
        ),
        sortingFn: sorting.number((row) => getUniqueDeviceCount(row.items)),
        meta: {
          dataTestId: `${dataTestPrefix}-devices`,
          width: 100,
        },
      }),
      groupedColumn.accessor((row) => getLastOccurred(row.items), {
        id: "last_occurred",
        header: "Last Occurred",
        cell: (prop) => formatDateTimeLocal(prop.getValue() ?? ""),
        sortingFn: sorting.datetime((row) => getLastOccurred(row.items)),
        meta: {
          dataTestId: `${dataTestPrefix}-last-occurred`,
          width: 200,
        },
      }),
      {
        id: "resolve_all",
        header: "",
        cell: ({ row }) =>
          userIsReadonly ? null : (
            <Button
              dataTestId="resolve-all-button"
              icon="checkmark"
              onClick={() => handleOpenResolveAll(row.original.items)}
              variant="outlined"
            >
              Resolve All
            </Button>
          ),
        enableSorting: false,
        meta: {
          align: "right",
          dataTestId: `${dataTestPrefix}-resolve-all`,
          disableWrapper: true,
          width: 140,
        },
      },
    ];
  }, [dataTestPrefix, handleOpenResolveAll, isResolvedTable, userIsReadonly]);

  const renderGroupedExpandedRow = useCallback(
    (row: GroupedTableRow<Incident>) => {
      const groupIds = new Set(row.items.map(({ id }) => String(id)));
      const groupSelectedItems = selectedItems.filter(({ id }) =>
        groupIds.has(String(id)),
      );
      const nestedTableProps = isResolvedTable
        ? { hideColumns: ["status"] }
        : userIsReadonly
          ? {}
          : {
              getIdentifier: ({ id }: Incident) => String(id),
              selectedItems: groupSelectedItems,
              setSelectedItems: (items: ReadonlyArray<Incident>) =>
                handleNestedSelectionChange(row.items, items),
            };

      const getRowProps = (incident: Incident) => {
        return {
          hover: true,
          onClick: (event: MouseEvent<HTMLTableRowElement>) => {
            const target = event.target as HTMLElement;
            const isVeneerCell = target.closest(
              'th[data-component="veneer-table-cell"], td[data-component="veneer-table-cell"]',
            );

            if (!isVeneerCell) {
              return;
            }

            if (target.closest("input[type='checkbox']")) {
              return;
            }

            if (
              target.closest(
                "a, button, [role='button'], [role='menuitem'], input, select, textarea, label",
              )
            ) {
              return;
            }

            handleOpenSingle(incident);
          },
        };
      };

      return (
        <DataTable
          columns={nestedColumns}
          dataTestId={{
            root: `${dataTestPrefix}-nested-table-${row.representative.alert_id}`,
            row: `${dataTestPrefix}-nested-row-${row.representative.alert_id}`,
            selectAll: `${dataTestPrefix}-nested-select-all-${row.representative.alert_id}`,
          }}
          getRowProps={getRowProps}
          initialPageSize={10}
          initialSort={{ id: "create_date", desc: true }}
          rows={row.items}
          {...nestedTableProps}
        />
      );
    },
    [
      dataTestPrefix,
      handleOpenSingle,
      handleNestedSelectionChange,
      isResolvedTable,
      nestedColumns,
      selectedItems,
      userIsReadonly,
    ],
  );

  const hiddenColumns = useMemo(() => {
    if (isResolvedTable) {
      return [];
    }

    const cols = new Set<string>();

    if (userIsReadonly) {
      cols.add("resolve_all");
    }

    return Array.from(cols);
  }, [isResolvedTable, userIsReadonly]);

  return (
    <>
      <Box ref={anchorRef}>
        <GroupByTable
          appliedFilters={appliedFilters}
          availableFilters={availableFilters}
          columns={nestedColumns}
          dataTestId={{
            appliedFilters: "triggered-alerts-applied-filters",
            availableFilters: "triggered-alerts-available-filters",
            root: `${dataTestPrefix}-table`,
            row: `${dataTestPrefix}-group-row`,
            savedFilters: "triggered-alerts-saved-filters",
            selectAll: `${dataTestPrefix}-group-select-all`,
          }}
          groupBy={["alert_id"]}
          groupByOptions={[{ id: "alert_id", label: "Alert" }]}
          groupedColumns={groupedColumns}
          hideColumns={hiddenColumns}
          hideGroupByControls
          initialPageSize={sizePerPage}
          initialSort={
            isResolvedTable
              ? { id: "last_resolved", desc: true }
              : { id: "last_occurred", desc: true }
          }
          renderGroupedExpandedRow={renderGroupedExpandedRow}
          rows={incidents}
          savedFilters={savedFilters}
          settingsKey={settingsKey}
          viewOptions={viewOptions}
        />
      </Box>
      {showBulkActionBar && (
        <BulkActionBar
          actions={bulkActionBarItems}
          anchorRef={anchorRef}
          dataTestId="grouped-incidents-bulk-action-bar"
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
      )}
    </>
  );
}

function getUniqueDeviceCount(incidents: ReadonlyArray<Incident>): number {
  return new Set(incidents.map(({ device_id }) => device_id)).size;
}

function getLastOccurred(incidents: ReadonlyArray<Incident>): string {
  let latest = "";

  incidents.forEach((incident) => {
    const candidate = incident.last_occurred;

    if (
      candidate &&
      (!latest || new Date(candidate).getTime() > new Date(latest).getTime())
    ) {
      latest = candidate;
    }
  });

  return latest;
}

function getLastResolved(incidents: ReadonlyArray<Incident>): string {
  let latest = "";

  incidents.forEach((incident) => {
    const candidate = incident.resolve_date;

    if (
      candidate &&
      (!latest || new Date(candidate).getTime() > new Date(latest).getTime())
    ) {
      latest = candidate;
    }
  });

  return latest;
}
